import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Error Boundary Component
 * Catches React rendering errors and displays a fallback UI
 * Logs errors for operational visibility
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log to console for debugging
    console.error('[ErrorBoundary] Component Error:', error);
    console.error('[ErrorBoundary] Component Stack:', errorInfo?.componentStack);
    
    // Log to backend for observability (if handler provided)
    if (this.props.onError) {
      this.props.onError(error, errorInfo, this.props.componentName);
    }
    
    // Log to API for operational visibility
    this.logErrorToApi(error, errorInfo);
  }
  
  logErrorToApi = async (error, errorInfo) => {
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
      
      if (!API || !token) return;
      
      await fetch(`${API}/api/phs/client-errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          component: this.props.componentName || 'Unknown',
          error_message: error?.message || String(error),
          error_stack: error?.stack,
          component_stack: errorInfo?.componentStack,
          url: window.location.href,
          timestamp: new Date().toISOString()
        })
      });
    } catch (logError) {
      // Silently fail - don't cause more errors
      console.warn('[ErrorBoundary] Failed to log error to API:', logError);
    }
  };

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  render() {
    if (this.state.hasError) {
      const { fallback, componentName, minimal } = this.props;
      const { error, errorInfo, showDetails } = this.state;
      
      // Custom fallback UI
      if (fallback) {
        return fallback({ error, errorInfo, retry: this.handleRetry });
      }
      
      // Minimal fallback for small components
      if (minimal) {
        return (
          <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-center">
            <AlertTriangle className="h-5 w-5 text-red-500 mx-auto mb-1" />
            <p className="text-xs text-red-600">Failed to load</p>
            <button 
              onClick={this.handleRetry}
              className="mt-2 text-xs text-red-600 hover:underline"
            >
              Retry
            </button>
          </div>
        );
      }
      
      // Standard fallback UI
      return (
        <div className="p-6 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="error-boundary-fallback">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-red-100">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-slate-800">
                Something went wrong
              </h3>
              <p className="text-sm text-slate-600 mt-1">
                {componentName ? `The ${componentName} component encountered an error.` : 'A component error occurred.'}
              </p>
              
              <div className="flex items-center gap-3 mt-4">
                <Button 
                  onClick={this.handleRetry}
                  size="sm"
                  className="gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Try Again
                </Button>
                <Button
                  onClick={this.toggleDetails}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {showDetails ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  {showDetails ? 'Hide' : 'Show'} Details
                </Button>
              </div>
              
              {showDetails && (
                <div className="mt-4 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-xs font-mono text-red-600 mb-2">
                    {error?.message || 'Unknown error'}
                  </p>
                  {errorInfo?.componentStack && (
                    <pre className="text-[10px] font-mono text-slate-500 overflow-auto max-h-32 whitespace-pre-wrap">
                      {errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC to wrap a component with ErrorBoundary
 * @param {Component} WrappedComponent - Component to wrap
 * @param {string} componentName - Name for logging
 * @param {object} options - { minimal, fallback, onError }
 */
export function withErrorBoundary(WrappedComponent, componentName, options = {}) {
  return function ErrorBoundaryWrapper(props) {
    return (
      <ErrorBoundary 
        componentName={componentName}
        minimal={options.minimal}
        fallback={options.fallback}
        onError={options.onError}
      >
        <WrappedComponent {...props} />
      </ErrorBoundary>
    );
  };
}

export default ErrorBoundary;
